import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * What an assessment's menu does (PRD-1703), and where focus goes after each
 * action, since the control the user came from often changes or leaves:
 *
 *   Retry      the menu changes (Uploading has none): focus the name
 *   Archive    asks first; Cancel returns to the trigger. Once archived the
 *              item may leave the view: its trigger, else `fallbackRef`
 *   Unarchive  straight away: its trigger, else `fallbackRef`
 *
 * The same shape as the Library's useDocumentActions. `detailActions` are the
 * same three from inside the assessment detail, where focus stays in the
 * dialog: they move nothing on the page behind it, and Archive's Cancel
 * returns to the button it was asked from.
 */
export function useAssessmentActions({ vm, rootRef, fallbackRef, onShowToast }) {
  const [pendingFocus, setPendingFocus] = useState(null); // { id, part: 'name' | 'menu' }
  const [confirming, setConfirming] = useState(null); // { assessment, opener }
  const { retry, archive, unarchive } = vm;

  useEffect(() => {
    if (!pendingFocus) return;
    const selector = pendingFocus.part === 'name'
      ? `[data-asm-focus="${pendingFocus.id}"]`
      : `[data-asm-menu="${pendingFocus.id}"] button`;
    const target = rootRef.current?.querySelector(selector) || fallbackRef.current;
    if (target) target.focus();
    setPendingFocus(null);
  }, [pendingFocus, rootRef, fallbackRef]);

  // Retry and Unarchive say what they did; each returns whether it ran.
  const runRetry = useCallback((assessment) => {
    const step = retry(assessment.id);
    if (step) onShowToast?.(step === 'processing' ? 'Retrying processing.' : 'Retrying upload.', 'success');
    return !!step;
  }, [retry, onShowToast]);
  const runUnarchive = useCallback((assessment) => {
    const done = unarchive(assessment.id);
    if (done) onShowToast?.('Assessment unarchived.', 'success');
    return !!done;
  }, [unarchive, onShowToast]);

  const actions = useMemo(() => ({
    onRetry: (assessment) => {
      if (runRetry(assessment)) setPendingFocus({ id: assessment.id, part: 'name' });
    },
    onArchive: (assessment) => {
      const opener = rootRef.current?.querySelector(`[data-asm-menu="${assessment.id}"] button`) || null;
      setConfirming({ assessment, opener });
    },
    onUnarchive: (assessment) => {
      if (runUnarchive(assessment)) setPendingFocus({ id: assessment.id, part: 'menu' });
    },
  }), [runRetry, runUnarchive, rootRef]);

  const detailActions = useMemo(() => ({
    onRetry: runRetry,
    onArchive: (assessment, opener) => setConfirming({ assessment, opener: opener || null }),
    onUnarchive: runUnarchive,
  }), [runRetry, runUnarchive]);

  const confirmArchive = useCallback(() => {
    if (!confirming) return;
    if (archive(confirming.assessment.id)) onShowToast?.('Assessment archived.', 'success');
    setConfirming(null);
  }, [confirming, archive, onShowToast]);

  const cancelArchive = useCallback(() => setConfirming(null), []);

  return { actions, detailActions, confirming, confirmArchive, cancelArchive, focusId: pendingFocus?.id ?? null };
}
